/**
 * Axios API client with JWT auth interceptors
 * Base URL: VITE_API_URL from .env (falls back to '/api/v1' for same-origin prod)
 */
import axios, { type AxiosError } from 'axios'
import { STORAGE_KEYS, getItemFrom, setItemIn, clearAuthFrom } from '../lib/storage'

const API_BASE = (import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL)
  ? `${import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL}/api/v1`
  : '/api/v1'

export const apiClient = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
})

apiClient.interceptors.request.use((config) => {
  // Admin sessions use per-tab sessionStorage; students use localStorage.
  // Prefer sessionStorage if present in this tab to avoid cross-account overwrites.
  const token =
    getItemFrom<string>(sessionStorage, STORAGE_KEYS.ACCESS_TOKEN) ??
    getItemFrom<string>(localStorage, STORAGE_KEYS.ACCESS_TOKEN)
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

function endSession() {
  // Best-effort clear in both stores, since the interceptor cannot safely
  // infer which one is currently active for this tab.
  clearAuthFrom(sessionStorage)
  clearAuthFrom(localStorage)
  window.dispatchEvent(new CustomEvent('auth:session-expired'))
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as typeof error.config & { _retry?: boolean }
    const url = originalRequest?.url ?? ''
    // Never intercept 401s from auth endpoints — those are simply
    // wrong credentials or expected failures, not expired sessions.
    const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/register') || url.includes('/auth/refresh')
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true
      const refreshTokenSession = getItemFrom<string>(sessionStorage, STORAGE_KEYS.REFRESH_TOKEN)
      const refreshTokenLocal = getItemFrom<string>(localStorage, STORAGE_KEYS.REFRESH_TOKEN)
      const refreshToken = refreshTokenSession ?? refreshTokenLocal
      if (!refreshToken) {
        // No refresh token to even try — bounce to login.
        endSession()
        return Promise.reject(error)
      }
      try {
        const { data } = await axios.post(`${API_BASE}/auth/refresh`, { refresh_token: refreshToken })
        // Backend returns access_token only. Keep existing refresh_token intact.
        const store = refreshTokenSession ? sessionStorage : localStorage
        setItemIn(store, STORAGE_KEYS.ACCESS_TOKEN, data.access_token)
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${data.access_token}`
        }
        return apiClient(originalRequest)
      } catch {
        endSession()
      }
    }
    return Promise.reject(error)
  }
)
